import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import getDownstreamSteps from 'analysis-tool/data/getDownstreamSteps';
import getLegacyStepFromAnalysis from 'analysis-tool/data/getStepFromAnalysis';
import {REGRESSION_TYPE_TO_READABLE} from 'analysis-tool/ui/charting/editor-sections/RegressionEditorSidebarSection';
import {assert} from 'Asserts';
import columnToTypeIcon from 'data-frames/util/columnToTypeIcon';
import {PipelineStepType, PipelineStepStatusCode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {getOriginOrTerminalStep, getParentSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {
  FONT_PROXIMA_12,
  FONT_PROXIMA_13,
  FONT_PROXIMA_14,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import * as Colors from 'ui-library/Colors';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import BenchlingDuotoneIcon from 'ui-library/icons/BenchlingDuotoneIcon';
import Menu from 'ui-library/menus/Menu';
import {OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

/** Get the "origin" step of the regression; i.e., the last step in a set of steps representing a top-level table. */
function getOriginStep(
  analysis: PipelineGraphQL,
  regressionStep: GetAnalysisPipelineStep
): GetAnalysisPipelineStep {
  // Every regression step branches from the top-level dataset via a dataset view step. Look at the
  // parent of the dataset view step to get the "origin" step with which the regression is associated.
  return getParentSteps(analysis, getOriginOrTerminalStep(analysis, regressionStep))[0];
}

/** Get the name of the top-level table from which the regression step originated. */
function getOriginStepDataFrameName(
  analysis: PipelineGraphQL,
  regressionStep: GetAnalysisPipelineStep
): string {
  const viewStep = getOriginOrTerminalStep(analysis, regressionStep);
  return viewStep.inputStepDatas[0].dataFrames[0].name;
}

function hasPartition(regressionStep: GetAnalysisPipelineStep): boolean {
  const {regressionConfig} = getPipelineStepConfig(regressionStep, PipelineStepType.REGRESSION, 0);

  return regressionConfig.partitionColumn != null;
}

/** Get the dropdown menu items for the selector.
 *
 * The menu is organized as:
 *
 * - <TOP_LEVEL_TABLE> (header)
 *   - <REGRESSION>
 *   - <REGRESSION>
 *   - <REGRESSION>
 * - <TOP_LEVEL_TABLE> (header)
 *   - <REGRESSION>
 *   - <REGRESSION>
 *
 */
function getOptionDatas(
  analysis: PipelineGraphQL,
  referenceStep: GetAnalysisPipelineStep
): Array<OptionData> {
  const regressionStepsByOriginStepId: Record<string, Array<GetAnalysisPipelineStep>> = {};

  for (const step of analysis.execution.steps) {
    if (
      step.definition.stepType === PipelineStepType.REGRESSION &&
      step.statusCode === PipelineStepStatusCode.SUCCEEDED &&
      ![...getDownstreamSteps(analysis, referenceStep.id)].some(
        (downstreamStep) => downstreamStep.id === step.id
      )
    ) {
      const originStep = getOriginStep(analysis, step);
      if (regressionStepsByOriginStepId[originStep.id] != null) {
        regressionStepsByOriginStepId[originStep.id].push(step);
      } else {
        regressionStepsByOriginStepId[originStep.id] = [step];
      }
    }
  }

  const optionDatas: Array<OptionData> = [];

  for (const [originStepId, regressionSteps] of Object.entries(regressionStepsByOriginStepId)) {
    optionDatas.push(
      {
        isHeader: true,
        name: getOriginStepDataFrameName(analysis, regressionSteps[0]),
      },
      ...regressionSteps.map((step) => ({
        name: <RegressionOption regressionStep={step} />,
        pureTextName: getPipelineStepConfig(step, PipelineStepType.REGRESSION, 0).name,
        value: step.id,
        extra: {
          headerId: originStepId,
        },
      }))
    );
  }

  return optionDatas;
}

function RegressionOption({regressionStep}: {regressionStep: GetAnalysisPipelineStep}): JSX.Element {
  const {name, regressionConfig} = getPipelineStepConfig(regressionStep, PipelineStepType.REGRESSION, 0);

  let partitionDisplayName;
  if (regressionConfig.partitionColumn != null) {
    const inputDataFrameColumns = regressionStep.inputStepDatas[0].dataFrames[0].allColumnsJson;
    partitionDisplayName = inputDataFrameColumns.find(
      (columnDef) => columnDef.systemName === regressionConfig.partitionColumn
    )?.displayName;
  }

  return (
    <div className={css(styles.regressionOption)}>
      <div className={css(styles.optionLeft)}>
        <BenchlingDuotoneIcon
          icon="chart-line"
          primaryColor={Colors.BLUE}
          secondaryColor={Colors.BLUE_LIGHTER}
        />
        <div className={css(styles.optionLeftText)}>
          <div className={css(styles.regressionName)}>{name}</div>
          <div className={css(styles.regressionType)}>
            {REGRESSION_TYPE_TO_READABLE[regressionConfig.modelType]}
          </div>
        </div>
      </div>
      {partitionDisplayName != null && (
        <div className={css(styles.optionRight)}>{`Series: ${partitionDisplayName}`}</div>
      )}
    </div>
  );
}

function SelectedRegressionOption({
  dataFrameName,
  regressionStep,
}: {
  dataFrameName: string;
  regressionStep: GetAnalysisPipelineStep;
}): JSX.Element {
  const {name} = getPipelineStepConfig(regressionStep, PipelineStepType.REGRESSION, 0);
  return (
    <div className={css(styles.regressionOption)}>
      <BenchlingDuotoneIcon
        icon="chart-line"
        primaryColor={Colors.BLUE}
        secondaryColor={Colors.BLUE_LIGHTER}
      />
      <div className={css(styles.optionLeftText)}>
        <div className={css(styles.regressionName)}>{`${name} | ${dataFrameName}`}</div>
      </div>
    </div>
  );
}

function SeriesDisplay({regressionStep}: {regressionStep: GetAnalysisPipelineStep}): JSX.Element {
  assert(
    hasPartition(regressionStep),
    'Expected this component to only be used on a regression step with a partition defined.'
  );

  const {regressionConfig} = getPipelineStepConfig(regressionStep, PipelineStepType.REGRESSION, 0);
  const inputDataFrameColumns = regressionStep.inputStepDatas[0].dataFrames[0].allColumnsJson;
  const partitionColumn = inputDataFrameColumns.find(
    (columnDef) => columnDef.systemName === regressionConfig.partitionColumn
  )!;

  return (
    <>
      <span className={css(styles.variableText)}>Series</span>
      <Chip
        size={ChipSize.SMALL}
        icon={columnToTypeIcon(partitionColumn)}
        text={partitionColumn.displayName}
        isReadonly={true}
      />
    </>
  );
}

export interface RegressionModelSelectorProps {
  analysis: PipelineGraphQL;
  inputStep: GetAnalysisPipelineStep | null;
  referenceStep: GetAnalysisPipelineStep;
  setInputStep: (newStep: GetAnalysisPipelineStep) => void;
}

export default function RegressionModelSelector({
  analysis,
  inputStep,
  referenceStep,
  setInputStep,
}: RegressionModelSelectorProps): JSX.Element {
  assert(
    inputStep == null || inputStep.definition.stepType === PipelineStepType.REGRESSION,
    'Expected this component to only be used with a regression step.'
  );

  const optionDatas = getOptionDatas(analysis, referenceStep);

  function getDisplayName(): React.ReactNode {
    if (inputStep == null) {
      return 'Search for a regression model';
    } else {
      return (
        <SelectedRegressionOption
          dataFrameName={getOriginStepDataFrameName(analysis, inputStep)}
          regressionStep={inputStep}
        />
      );
    }
  }

  return (
    <div className={css(styles.wrapper)}>
      <div className={css(styles.regressionSelector)}>
        <span className={css(styles.variableText)}>Select regression model</span>
        <SelectDropdown
          menu={
            <SearchableMenu
              onSelect={(newSelectedId) => {
                setInputStep(getLegacyStepFromAnalysis(analysis, newSelectedId));
              }}
              optionDatas={optionDatas}
              selectedValue={inputStep?.id}
              width={Menu.WIDTHS.FULL}
            />
          }
          displayName={getDisplayName()}
          buttonSize={ButtonSize.MEDIUM}
          dataTestAttrs={{
            component: 'StepConfigEditor',
            element: 'dropdown',
            key: 'modelType',
          }}
        />
      </div>
      {inputStep != null && hasPartition(inputStep) && (
        <div className={css(styles.seriesDisplay)}>
          <SeriesDisplay regressionStep={inputStep} />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  optionLeft: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',
  },
  optionLeftText: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 8,
  },
  optionRight: {
    ...FONT_PROXIMA_14,
    color: Colors.GRAY_DARK,
    alignContent: 'center',
  },
  regressionName: {
    ...FONT_PROXIMA_13,
    color: Colors.NEAR_BLACK,
  },
  regressionOption: {
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'space-between',
    alignItems: 'center',
  },
  regressionSelector: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  regressionType: {
    ...FONT_PROXIMA_12,
    color: Colors.GRAY_DARK,
  },
  seriesDisplay: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 16,
  },
  variableText: {
    ...FONT_PROXIMA_14,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    marginBottom: 4,
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: 8,
  },
});
